import type { SimulatorService } from './services/simulator-v2/simulator.types'
import type { UseBankStatementParams } from './store-v2/bank-statement/useBankStatement'
import type { UseFinancingsParams } from './store-v2/financings/useFinancings'
import type { UsePartnersParams } from './store-v2/partners/usePartners'
import { QueryClient } from '@tanstack/vue-query'
import { filterAndFormatParams } from './utils/objects'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 2,
      retryDelay: 1000,
      staleTime: 1000 * 60 * 3, // 3 minutes
      gcTime: 1000 * 60 * 5, // 5 minutes
    },
  },
})

export const queryKeys = {
  AUTH: {
    LOGIN: ['AUTH', 'LOGIN'],
    LOGOUT: ['AUTH', 'LOGOUT'],
    STATUS: ['AUTH', 'STATUS'],
  },
  USER: () => ['USER'],
  SF_PLUS: {
    STATUS: ['SF_PLUS', 'STATUS'],
  },
  FINANCINGS: {
    LIST: (params: UseFinancingsParams) => ['FINANCING', 'LIST', ...filterAndFormatParams(params)],
  },
  FINANCING: {
    DETAILS: (id: string) => ['FINANCING', 'DETAILS', id],
    SYSTEM_TYPE: (id: string) => ['FINANCING', 'SYSTEM_TYPE', id],
  },
  PARTNERS: {
    LIST: (params: UsePartnersParams) => ['PARTNERS', 'LIST', ...filterAndFormatParams(params)],
    DETAILS: (partnerId: string) => ['PARTNERS', 'DETAILS', partnerId],
  },
  BANK_STATEMENT: {
    LIST: (params: UseBankStatementParams) => ['BANK_STATEMENT', 'LIST', ...filterAndFormatParams(params)],
  },
  ADDRESS: {
    BY_CEP: (cep: Ref<string | undefined>) => ['ADDRESS', 'BY_CEP', cep.value],
  },
  CONTRACT_BIOMETRY: {
    DATA: (projectId: string) => ['CONTRACT_BIOMETRY', 'DATA', projectId],
    CONFIRM: (projectId: string) => ['CONTRACT_BIOMETRY', 'CONFIRM', projectId],
    SEND: ['CONTRACT_BIOMETRY', 'SEND'],
    RESEND: ['CONTRACT_BIOMETRY', 'RESEND'],
    RESEND_BIOMETRY: ['CONTRACT_BIOMETRY', 'RESEND_BIOMETRY'],
    RESEND_CONTRACT: ['CONTRACT_BIOMETRY', 'RESEND_CONTRACT'],
    CANCEL: ['CONTRACT_BIOMETRY', 'CANCEL'],
    CANCEL_CONTRACT: ['CONTRACT_BIOMETRY', 'CANCEL_CONTRACT'],
  },
  REGISTRATION: {
    DATA: (projectId: string) => ['REGISTRATION', 'DATA', projectId],
    GRID_ACCESS_APPROVAL: (projectId: string, city: string, state: string) => ['REGISTRATION', 'GRID_ACCESS_APPROVAL', projectId, city, state],
    COMPANY: (id: string) => ['REGISTRATION', 'COMPANY', id],
    CUSTOMER: (id: string) => ['REGISTRATION', 'CUSTOMER', id],
    ENERGY_CONCESSIONAIRES: (projectId: Ref<string>) => ['REGISTRATION', 'ENERGY_CONCESSIONAIRES', projectId.value],
    UPDATE: {
      FILE_UPLOAD: (projectId: string) => ['REGISTRATION', 'UPDATE', 'FILE_UPLOAD', projectId],
      DATA: (projectId: string) => ['REGISTRATION', 'UPDATE', 'DATA', projectId],
    },
    REGISTRATION_CONTACT_CHANGE: ['REGISTRATION', 'REGISTRATION_CONTACT_CHANGE'],
    FORM_ANALYSIS_CANCEL: [
      'REGISTRATION',
      'FORM_ANALYSIS_CANCEL',
    ],
  },
  FORMALIZATION_V2: (projectId: string) => ['FORMALIZATION_V2', projectId],
  PROJECTS: (params: SimulatorService.ProjectsRequest) => ['PROJECTS', ...filterAndFormatParams(params)],
  PROJECT: {
    LIST: (params: SimulatorService.ProjectsRequest) => ['PROJECT', 'LIST', ...filterAndFormatParams(params)],
    DETAILS: (id: string) => ['PROJECT', 'DETAILS', id],
    PROPOSAL_CHANGES: (id: string) => ['PROJECT', 'PROPOSAL_CHANGES', id],
    PROPOSAL_UPDATE: (id: string) => ['PROJECT', 'PROPOSAL_UPDATE', id],
    CONTRACT_SIGNED: (id: string) => ['PROJECT', 'CONTRACT_SIGNED', id],
  },
  PROJECTS_PARTNER_OVERVIEW: (partnerId: Ref<string | undefined | null>) => ['PROJECTS_PARTNER_OVERVIEW', partnerId.value],
  OPPORTUNITIES: (params: SimulatorService.GetOpportunitiesRequest) => ['OPPORTUNITIES', ...filterAndFormatParams(params)],

  ANALYSIS_BILL: ['ANALYSIS_BILL'],
  FINANCING_TYPES: ['FINANCING_TYPES'],

  LEGACY_FINANCING: (projectId: string) => ['LEGACY_FINANCING', projectId],

  INSTALLATION: {
    DETAILS: (projectId: Ref<string | undefined | null>) => ['INSTALLATION', 'DETAILS', projectId.value],
    SAVE_HARDWARE: (projectId: Ref<string>) => ['INSTALLATION', 'SAVE_HARDWARE', projectId.value],
    COMBOS_HARDWARE: (projectId: Ref<string>) => ['INSTALLATION', 'COMBOS_HARDWARE', projectId.value],
    UPLOAD_FILE: ['INSTALLATION', 'UPLOAD_FILE'],
    VALIDATE_TOTAL_INVERTERS_POWER: (projectId: string, power: number) => ['INSTALLATION', 'VALIDATE_TOTAL_INVERTERS_POWER', projectId, power],
    LABELS: (projectId: Ref<string>) => ['INSTALLATION', 'LABELS', projectId.value],
    SECTION_DELETE: (installationId: string) => ['INSTALLATION', 'SECTION_DELETE', installationId],
    COMMISSIONING_STATUS: (installationId: string) => ['INSTALLATION', 'COMMISSIONING_STATUS', installationId],
    COMMISSIONING_REQUEST: (installationId: string) => ['INSTALLATION', 'COMMISSIONING_REQUEST', installationId],
    COMMISSIONING_RESET_CUSTOMER_REJECTION: (projectId: string) =>
      ['INSTALLATION', 'COMMISSIONING_RESET_CUSTOMER_REJECTION', projectId],
    BIOMETRIC_VALIDATION: (projectId: string) => ['INSTALLATION', 'BIOMETRIC_VALIDATION', projectId],
    REGISTER_PARTNER_CREDENTIALS_CHECK: (brand: string) => ['INSTALLATION', 'REGISTER_PARTNER_CREDENTIALS_CHECK', brand],
    INVERTERS_BRANDS: ['INSTALLATION', 'INVERTERS_BRANDS'],
    ACCESS_USER_CREDENTIALS: (brand: Ref<string | undefined | null>) => ['INSTALLATION', 'ACCESS_USER_CREDENTIALS', brand.value],
    CHANGE_CREDENTIALS: (brand: string) => ['INSTALLATION', 'CHANGE_CREDENTIALS', brand],
  },

  HARDWARE: {
    FORM: (projectId: string) => ['HARDWARE', 'FORM', projectId],
    DRAFT: ['HARDWARE', 'DRAFT'],
    VALIDATION: ['HARDWARE', 'VALIDATION'],
    MIRROR_STATUS: (projectId: string, skip: boolean) => ['HARDWARE', 'MIRROR_STATUS', projectId, skip],
    MIRROR_UPLOAD: ['HARDWARE', 'MIRROR_UPLOAD'],
    SAVE_MIRROR: (projectId: string) => ['HARDWARE', 'SAVE_MIRROR', projectId],
    HISTORY: (projectId: string) => ['HARDWARE', 'HISTORY', projectId],
  },

  SIMULATION: {
    GET_SHOPPING_CART_ID: (projectId: string) => ['SIMULATION', 'GET_SHOPPING_CART_ID', projectId],
    PUT_SHOPPING_CART_ID: ['SIMULATION', 'PUT_SHOPPING_CART_ID'],
    PDF_DOWNLOAD: (projectId: string) => ['SIMULATION', 'PDF_DOWNLOAD', projectId],
  },
  PAYMENT_MODEL: {
    DATA: (projectId: string) => ['PAYMENT_MODEL', 'DATA', projectId],
    SUPPLIERS: ['PAYMENT_MODEL', 'SUPPLIERS'],
    UPLOAD_FILE: ['PAYMENT_MODEL', 'UPLOAD_FILE'],
    DELETE_FILE: ['PAYMENT_MODEL', 'DELETE_FILE'],
    SEND_BILLING_LETTER: ['PAYMENT_MODEL', 'SEND_BILLING_LETTER'],
    UPDATE: ['PAYMENT_MODEL', 'UPDATE'],
    DOWNLOAD_RECEIPT: (projectId: string) => ['PAYMENT_MODEL', 'DOWNLOAD_RECEIPT', projectId],
    AVAILABLE_ORDERS: (projectId: string) => ['PAYMENT_MODEL', 'AVAILABLE_ORDERS', projectId],
  },
  NOTIFICATIONS: {
    LIST: (userId: number, cursor?: string) => ['NOTIFICATIONS', 'LIST', userId, cursor],
  },
  SOLFACILPLUS: {
    INFO: (partnerId: string) => ['SOLFACILPLUS', 'INFO', partnerId],
  },
  REVALIDATIONS: {
    SUMMARY: ['REVALIDATIONS', 'SUMMARY'] as const,
    LIST: ['REVALIDATIONS', 'LIST'] as const,
  },
  INCENTIVES: {
    TARGET_SUMMARY: (targetId: string, targetType: string, campaignType: string) =>
      ['INCENTIVES', 'TARGET_SUMMARY', targetId, targetType, campaignType] as const,
  },
  MY_WALLET: {
    PAYMENT_MODEL: (partnerId: string) => ['MY_WALLET', 'PAYMENT_MODEL', partnerId] as const,
  },
}

export default queryClient
